import type {
  AssessmentQuestion,
  CompatibilityResult,
  Match,
  OCEANScores,
  SubScore,
  VibeProfile,
} from "../models";
import { daysFromToday } from "../mockDate";

/**
 * Port of `MockMatchingService.swift`.
 *
 * The only mock that computes rather than looks up: scores come out of an OCEAN
 * distance function, nudged by a zodiac multiplier, and the narrative is
 * assembled from the score tier, the strongest sub-dimension, and the MBTI
 * pairing. Worth keeping intact — a hardcoded "94% match" is exactly the number
 * an investor asks about, and this one can be explained.
 *
 * Vibe text is inlined from `Localizable.xcstrings` (the `vibe_*` keys). The
 * mini app has no string catalogue, so the English values live here.
 */

const delay = (seconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, seconds * 1000));

interface MockProfile {
  id: string;
  displayName: string;
  username: string;
  profilePhotoURL?: string;
  school: string;
  major: string;
  year: string;
  isVerified: boolean;
  ocean: OCEANScores;
  mbti: string;
  enneagram: string;
  zodiac: string;
  vibe: string;
  /**
   * Deliberately untranslated: these read as tags the student typed themselves,
   * and translating "shounen" into anything is worse than leaving it alone.
   */
  interests: string[];
}

const ALEX: MockProfile = {
  id: "user-001",
  displayName: "Alex Chen",
  username: "alexchen",
  profilePhotoURL: "Characters/alex/alex-profile",
  school: "UT Austin",
  major: "Computer Science",
  year: "2029",
  isVerified: true,
  ocean: {
    openness: 0.85,
    conscientiousness: 0.65,
    extraversion: 0.72,
    agreeableness: 0.8,
    neuroticism: 0.35,
  },
  mbti: "ENFP",
  enneagram: "7w6",
  zodiac: "Sagittarius",
  vibe: "Curious and quick to say yes — Alex collects side projects the way other people collect playlists. Warm with new people, and allergic to a boring Tuesday.",
  interests: [
    "anime",
    "isekai",
    "indie games",
    "late-night coding",
    "live music",
    "bouldering",
    "ramen",
  ],
};

/**
 * An array rather than the Swift original's dictionary. `getMatches` iterated
 * the dictionary before sorting, and Swift dictionary order is not stable
 * between runs — so equal scores could reorder, and `matchedAt` (assigned by
 * post-sort index) with them.
 */
const PROFILES: MockProfile[] = [
  {
    id: "u2",
    displayName: "Maya Rodriguez",
    username: "mayarodriguez",
    profilePhotoURL: "Characters/maya/maya-profile",
    school: "UT Austin",
    major: "Film Studies",
    year: "2029",
    isVerified: true,
    ocean: {
      openness: 0.87,
      conscientiousness: 0.62,
      extraversion: 0.75,
      agreeableness: 0.82,
      neuroticism: 0.22,
    },
    mbti: "ENFJ",
    enneagram: "2w3",
    zodiac: "Taurus",
    vibe: "Maya notices the things everyone else walks past, then makes you see them too. Genuinely steady company — the friend who remembers what you were nervous about last week.",
    interests: [
      "anime",
      "slice of life",
      "film photography",
      "A24 movies",
      "thrifting",
      "live music",
      "ramen",
    ],
  },
  {
    id: "u1",
    displayName: "Jamie Park",
    username: "jamiepark",
    profilePhotoURL: "Characters/jamie/jamie-profile",
    school: "UT Austin",
    major: "Computer Science",
    year: "2029",
    isVerified: true,
    ocean: {
      openness: 0.8,
      conscientiousness: 0.72,
      extraversion: 0.6,
      agreeableness: 0.68,
      neuroticism: 0.38,
    },
    mbti: "INTP",
    enneagram: "5w4",
    zodiac: "Aries",
    vibe: "Jamie thinks in systems and will happily disappear into a problem for six hours. Quiet at first, then very funny once the conversation gets specific.",
    interests: [
      "anime",
      "mecha",
      "competitive programming",
      "mechanical keyboards",
      "late-night coding",
      "indie games",
      "ramen",
    ],
  },
  {
    id: "u4",
    displayName: "Sophie Liu",
    username: "sophieliu",
    profilePhotoURL: "Characters/sophie/sophie-profile",
    school: "UT Austin",
    major: "Psychology",
    year: "2029",
    isVerified: true,
    ocean: {
      openness: 0.73,
      conscientiousness: 0.68,
      extraversion: 0.5,
      agreeableness: 0.76,
      neuroticism: 0.45,
    },
    mbti: "INFJ",
    enneagram: "4w5",
    zodiac: "Leo",
    vibe: "Sophie reads a room before she says anything, and what she says usually lands. Soft-spoken but decisive — she keeps a small circle on purpose.",
    interests: [
      "anime",
      "shoujo",
      "psychology podcasts",
      "café hopping",
      "vintage bookstores",
      "live music",
    ],
  },
  {
    id: "u10",
    displayName: "Zara Mitchell",
    username: "zaramitchell",
    profilePhotoURL: "Characters/zara/zara-profile",
    school: "UT Austin",
    major: "Political Science",
    year: "2029",
    isVerified: true,
    ocean: {
      openness: 0.78,
      conscientiousness: 0.58,
      extraversion: 0.78,
      agreeableness: 0.62,
      neuroticism: 0.42,
    },
    mbti: "ENTP",
    enneagram: "3w4",
    zodiac: "Aquarius",
    vibe: "Zara argues for sport and means none of it personally. She'll take the other side just to see what's underneath your position — and she'll change her mind if you're right.",
    interests: [
      "anime",
      "psychological thrillers",
      "debate",
      "campus politics",
      "protest photography",
      "spoken word",
    ],
  },
  {
    id: "u5",
    displayName: "Darius Johnson",
    username: "dariusjohnson",
    profilePhotoURL: "Characters/darius/darius-profile",
    school: "UT Austin",
    major: "Business",
    year: "2029",
    isVerified: true,
    ocean: {
      openness: 0.68,
      conscientiousness: 0.6,
      extraversion: 0.88,
      agreeableness: 0.65,
      neuroticism: 0.42,
    },
    mbti: "ESTP",
    enneagram: "7w8",
    zodiac: "Sagittarius",
    vibe: "Darius turns any room into a group activity within ten minutes. High energy, low drama, and he'll have three plans running before you've picked one.",
    interests: [
      "anime",
      "shounen",
      "pickup basketball",
      "sneaker culture",
      "hip-hop production",
      "live music",
    ],
  },
  {
    id: "u6",
    displayName: "Priya Patel",
    username: "priyapatel",
    profilePhotoURL: "Characters/priya/priya-profile",
    school: "UT Austin",
    major: "Biology",
    year: "2029",
    isVerified: true,
    ocean: {
      openness: 0.88,
      conscientiousness: 0.82,
      extraversion: 0.45,
      agreeableness: 0.72,
      neuroticism: 0.25,
    },
    mbti: "INTJ",
    enneagram: "1w2",
    zodiac: "Virgo",
    vibe: "Priya plans in months, not evenings, and it shows. Reserved until she trusts you, then relentlessly generous with her time and her notes.",
    interests: [
      "anime",
      "seinen",
      "lab research",
      "birdwatching",
      "bullet journaling",
      "bouldering",
    ],
  },
  {
    id: "u8",
    displayName: "Brittany Okafor",
    username: "brittanyokafor",
    profilePhotoURL: "Characters/brittany/brittany-profile",
    school: "UT Austin",
    major: "Communications",
    year: "2029",
    isVerified: true,
    ocean: {
      openness: 0.65,
      conscientiousness: 0.5,
      extraversion: 0.68,
      agreeableness: 0.7,
      neuroticism: 0.58,
    },
    mbti: "ENFP",
    enneagram: "4w3",
    zodiac: "Scorpio",
    vibe: "Brittany feels things at full volume and makes that fun to be around. Big-hearted, a little scattered, and the first to check in when you go quiet.",
    interests: [
      "anime",
      "magical girl",
      "podcasting",
      "astrology",
      "playlist curation",
      "live music",
    ],
  },
  {
    id: "u7",
    displayName: "Carlos Reyes",
    username: "carlosreyes",
    profilePhotoURL: "Characters/carlos/carlos-profile",
    school: "UT Austin",
    major: "Marketing",
    year: "2029",
    isVerified: false,
    ocean: {
      openness: 0.45,
      conscientiousness: 0.45,
      extraversion: 0.75,
      agreeableness: 0.52,
      neuroticism: 0.65,
    },
    mbti: "ESFP",
    enneagram: "7w6",
    zodiac: "Libra",
    vibe: "Carlos is happiest mid-plan with people around him. Easy company and quick to laugh, though he takes things to heart more than he lets on.",
    interests: ["anime", "comedy anime", "mobile gaming", "soccer", "street food", "indie games"],
  },
  {
    id: "u3",
    displayName: "Tyler Washington",
    username: "tylerwashington",
    profilePhotoURL: "Characters/tyler/tyler-profile",
    school: "UT Austin",
    major: "Kinesiology",
    year: "2029",
    isVerified: false,
    ocean: {
      openness: 0.32,
      conscientiousness: 0.78,
      extraversion: 0.82,
      agreeableness: 0.4,
      neuroticism: 0.75,
    },
    mbti: "ESTJ",
    enneagram: "8w7",
    zodiac: "Gemini",
    vibe: "Tyler runs on structure and holds himself to it harder than he holds anyone else. Direct to the point of blunt, and more anxious underneath than he'd admit.",
    interests: [
      "anime",
      "sports anime",
      "powerlifting",
      "fantasy football",
      "meal prep",
      "bouldering",
    ],
  },
  {
    id: "u9",
    displayName: "Ethan Nguyen",
    username: "ethannguyen",
    school: "UT Austin",
    major: "Accounting",
    year: "2029",
    isVerified: false,
    ocean: {
      openness: 0.3,
      conscientiousness: 0.88,
      extraversion: 0.38,
      agreeableness: 0.5,
      neuroticism: 0.65,
    },
    mbti: "ISTJ",
    enneagram: "6w5",
    zodiac: "Capricorn",
    vibe: "Ethan is the one who actually read the syllabus, and he'll send you the deadline you missed. Slow to open up, completely reliable once he does.",
    interests: [
      "anime",
      "historical anime",
      "model kits",
      "personal finance",
      "badminton",
      "ramen",
    ],
  },
];

/* ---------------- Algorithm ---------------- */

/**
 * Similarity on four traits, plus a shared-calm term.
 *
 * O/C/E/A score on closeness — the 1.5 coefficient means a 0.67 gap zeroes a
 * dimension out. Neuroticism is the exception: it is not about matching, it is
 * the *pair's* average calm, so two anxious people score low together however
 * alike they are.
 */
function computeBaseScore(a: OCEANScores, b: OCEANScores): number {
  const near = (x: number, y: number) => Math.max(0, 1 - 1.5 * Math.abs(x - y));
  return (
    near(a.openness, b.openness) * 0.25 +
    near(a.conscientiousness, b.conscientiousness) * 0.15 +
    near(a.extraversion, b.extraversion) * 0.2 +
    near(a.agreeableness, b.agreeableness) * 0.25 +
    (1 - (a.neuroticism + b.neuroticism) / 2) * 0.15
  );
}

const ZODIAC_ELEMENT: Record<string, string> = {
  Aries: "Fire",
  Leo: "Fire",
  Sagittarius: "Fire",
  Taurus: "Earth",
  Virgo: "Earth",
  Capricorn: "Earth",
  Gemini: "Air",
  Libra: "Air",
  Aquarius: "Air",
  Cancer: "Water",
  Scorpio: "Water",
  Pisces: "Water",
};

/** Small enough to never overturn the personality score — a nudge, not a verdict. */
function zodiacMultiplier(sign1: string, sign2: string): number {
  const e1 = ZODIAC_ELEMENT[sign1] ?? "Fire";
  const e2 = ZODIAC_ELEMENT[sign2] ?? "Fire";
  if (e1 === e2) return 1.08;
  const pair = [e1, e2].sort().join("/");
  if (pair === "Air/Fire" || pair === "Earth/Water") return 1.05; // complementary
  if (pair === "Fire/Water" || pair === "Air/Earth") return 0.97; // friction
  return 1.0;
}

function tieredLabel(score: number, high: number, mid: number, labels: [string, string, string]) {
  if (score >= high) return labels[0];
  if (score >= mid) return labels[1];
  return labels[2];
}

function generateSubScores(a: OCEANScores, b: OCEANScores): SubScore[] {
  const near = (x: number, y: number) => Math.max(0, 1 - 1.5 * Math.abs(x - y));

  const intellectual = near(a.openness, b.openness);
  const social = near(a.extraversion, b.extraversion);
  const communication = near(a.agreeableness, b.agreeableness);
  const stability = 1 - (a.neuroticism + b.neuroticism) / 2;

  return [
    {
      dimension: "Intellectual Curiosity",
      score: intellectual,
      label: tieredLabel(intellectual, 0.8, 0.55, [
        "Both love exploring new ideas and perspectives",
        "Some overlap in intellectual interests",
        "Different approaches to curiosity and novelty",
      ]),
    },
    {
      dimension: "Social Energy",
      score: social,
      label: tieredLabel(social, 0.8, 0.55, [
        "Very similar social energy levels",
        "Can balance each other socially",
        "Different social preferences to navigate",
      ]),
    },
    {
      dimension: "Communication",
      score: communication,
      label: tieredLabel(communication, 0.8, 0.55, [
        "Communication styles naturally align",
        "Communication works with some effort",
        "May need patience in communication",
      ]),
    },
    {
      dimension: "Emotional Stability",
      score: stability,
      label: tieredLabel(stability, 0.7, 0.5, [
        "Strong emotional grounding together",
        "Moderate emotional balance as a pair",
        "Emotional dynamics may need attention",
      ]),
    },
  ];
}

function generateNarrative(
  name: string,
  score: number,
  subScores: SubScore[],
  alexMBTI: string,
  otherMBTI: string,
): string {
  let opening: string;
  if (score >= 0.88)
    opening = `You and ${name} have an exceptional connection — this is a rare level of compatibility.`;
  else if (score >= 0.8)
    opening = `You and ${name} share a strong natural chemistry that could lead to a great friendship.`;
  else if (score >= 0.7)
    opening = `You and ${name} have a solid foundation with real potential to click.`;
  else if (score >= 0.55)
    opening = `You and ${name} have some common ground — give it a chance and you might surprise each other.`;
  else
    opening = `You and ${name} are pretty different, but that can be a strength if you're both open to it.`;

  const best = subScores.reduce((a, b) => (b.score > a.score ? b : a));
  const strength =
    best.dimension === "Intellectual Curiosity"
      ? "Your shared love of learning and ideas is where you really shine together."
      : best.dimension === "Social Energy"
        ? "You both bring great social energy — expect plenty of good times."
        : best.dimension === "Communication"
          ? "Your communication styles mesh really well, which makes everything easier."
          : "You share a grounded, steady vibe that builds trust naturally.";

  const introvert1 = alexMBTI.startsWith("I");
  const introvert2 = otherMBTI.startsWith("I");
  const intuitive1 = alexMBTI[1] === "N";
  const intuitive2 = otherMBTI[1] === "N";

  let detail: string;
  if (intuitive1 && intuitive2) {
    detail =
      "You're both big-picture thinkers who love exploring possibilities — conversations will never be boring.";
  } else if (introvert1 !== introvert2) {
    detail =
      "One of you recharges solo while the other thrives in crowds — that balance keeps things interesting.";
  } else if (!introvert1 && !introvert2) {
    detail =
      "You're both outgoing and energized by people — expect spontaneous adventures together.";
  } else {
    detail = "Your personalities complement each other in ways that create a natural balance.";
  }

  return `${opening} ${strength} ${detail}`;
}

/**
 * Tags both people listed, in the viewer's own order so what they care about
 * reads first. Case-insensitive because these are free text — a tag typed as
 * "Anime" is the same thing.
 */
function sharedInterests(mine: string[], theirs: string[]): string[] {
  const set = new Set(theirs.map((t) => t.toLowerCase()));
  return mine.filter((t) => set.has(t.toLowerCase()));
}

function computeResult(profile: MockProfile) {
  const base = computeBaseScore(ALEX.ocean, profile.ocean);
  const zodiac = zodiacMultiplier(ALEX.zodiac, profile.zodiac);
  const score = Math.min(0.99, base * zodiac);
  const subScores = generateSubScores(ALEX.ocean, profile.ocean);
  return {
    score,
    zodiac,
    subScores,
    narrative: generateNarrative(profile.displayName, score, subScores, ALEX.mbti, profile.mbti),
    shared: sharedInterests(ALEX.interests, profile.interests),
  };
}

/* ---------------- Service ---------------- */

const LIKERT = ["Strongly Disagree", "Disagree", "Neutral", "Agree", "Strongly Agree"];

function question(id: string, text: string, dimension: string): AssessmentQuestion {
  return {
    id,
    text,
    dimension,
    options: LIKERT.map((label, i) => ({ id: `${id}o${i + 1}`, text: label, value: i + 1 })),
  };
}

export class MockMatchingService {
  async getAssessmentQuestions(): Promise<AssessmentQuestion[]> {
    await delay(0.5);
    return [
      question("q1", "I enjoy trying new and unfamiliar experiences.", "openness"),
      question("q2", "I keep my spaces organized and tidy.", "conscientiousness"),
      question(
        "q3",
        "I feel energized after spending time with a large group of people.",
        "extraversion",
      ),
      question("q4", "I go out of my way to make others feel comfortable.", "agreeableness"),
      question("q5", "I often worry about things that might go wrong.", "neuroticism"),
    ];
  }

  async submitAssessment(_answers: Record<string, number>): Promise<VibeProfile> {
    await delay(1.0);
    return {
      id: "vp-001",
      username: ALEX.username,
      vibeAnalysisText: ALEX.vibe,
      profileDepth: 3,
      oceanScores: ALEX.ocean,
      mbtiType: ALEX.mbti,
      enneagramType: ALEX.enneagram,
    };
  }

  async getMatches(page: number, limit: number): Promise<Match[]> {
    await delay(0.5);

    const ranked = PROFILES.map((profile) => ({ profile, ...computeResult(profile) })).sort(
      (a, b) => b.score - a.score,
    );

    const start = page * limit;
    if (start >= ranked.length) return [];

    return ranked.slice(start, start + limit).map((entry, i) => ({
      id: `m${start + i + 1}`,
      user: {
        id: entry.profile.id,
        displayName: entry.profile.displayName,
        profilePhotoURL: entry.profile.profilePhotoURL,
        school: entry.profile.school,
        major: entry.profile.major,
        year: entry.profile.year,
      },
      overallScore: entry.score,
      zodiacMultiplier: entry.zodiac,
      matchedAt: daysFromToday(-(start + i + 1), 10, 0),
    }));
  }

  async getCompatibility(userId: string): Promise<CompatibilityResult> {
    await delay(0.5);
    const profile = PROFILES.find((p) => p.id === userId);
    if (!profile) return { overallScore: 0.5, subScores: [] };

    const result = computeResult(profile);
    return {
      overallScore: result.score,
      subScores: result.subScores,
      narrative: result.narrative,
      zodiacMultiplier: result.zodiac,
      sharedInterests: result.shared.length > 0 ? result.shared : undefined,
    };
  }

  async getVibeProfile(userId?: string): Promise<VibeProfile | null> {
    await delay(0.3);
    if (userId === undefined) {
      return {
        id: "vp-001",
        username: ALEX.username,
        vibeAnalysisText: ALEX.vibe,
        profileDepth: 3,
        oceanScores: ALEX.ocean,
        mbtiType: ALEX.mbti,
        enneagramType: ALEX.enneagram,
        zodiacSign: ALEX.zodiac,
        interests: ALEX.interests,
      };
    }

    const profile = PROFILES.find((p) => p.id === userId);
    if (!profile) return null;
    return {
      id: `vp-${profile.id}`,
      username: profile.username,
      vibeAnalysisText: profile.vibe,
      profileDepth: 2,
      oceanScores: profile.ocean,
      mbtiType: profile.mbti,
      enneagramType: profile.enneagram,
      zodiacSign: profile.zodiac,
      interests: profile.interests,
    };
  }

  async updateVibeCheckPreferences(
    _allowedTypes: string[],
    _permissions: Record<string, string[]>,
  ): Promise<void> {
    await delay(0.3);
  }
}
